import { FC, useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Button,
  Checkbox,
  Divider,
  InputSelect,
  Label,
  RadioGroup,
  RadioGroupItem,
  Textarea,
} from '@features/ui';
import {
  ApprovePayoutRequestFormData,
  PaAccounts,
  schemaApprovePayoutRequest,
  TPayout,
} from '@src/types';
import { formatCurrency, formatDate } from '@src/lib/utils';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { getPayoutStatus, payoutAction } from '@src/services/payout.ts';
import { CloseIcon } from '@features/ui/icons';
import { toast } from 'react-toastify';

export const ApprovePayoutRequest: FC<{
  item?: TPayout;
  refreshData?: () => Promise<void>;
  onOpenChange: (open: boolean) => void;
}> = ({ item, onOpenChange, refreshData }) => {
  const [paAccounts, setPaAccounts] = useState<PaAccounts | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ApprovePayoutRequestFormData>({
    resolver: yupResolver(schemaApprovePayoutRequest),
  });

  const approveAction = watch('approve_action');
  const paymentMethod = watch('payment_method');

  const fetchPayoutStatus = async (account_id: number) => {
    const res = await getPayoutStatus(account_id);

    setPaAccounts(res.data);
  };

  useEffect(() => {
    if (!item?.leeloo_account_id) return;
    fetchPayoutStatus(item.leeloo_account_id).then();
  }, [item?.leeloo_account_id]);

  useEffect(() => {
    if (!item?.payout_method) return;
    reset({
      payment_method: item?.payout_method ?? undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.payout_method]);

  const handleOpenChange = (open: boolean) => {
    reset({
      amount_is_correct: undefined,
      approve_action: undefined,
      payment_method: undefined,
      payment_method_note: undefined,
      make_rithmic_deduction: undefined,
      deny_reason: undefined,
    });
    onOpenChange(open);
  };

  const handleSubmitForm = async (data: ApprovePayoutRequestFormData) => {
    if (!item) return;
    try {
      await payoutAction({
        ...data,
        payout_id: item?.id,
        current_user_id: item?.leeloo_user_id,
      });
      await refreshData?.();
      handleOpenChange(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.message);
    }
  };

  return (
    <form autoComplete="off" onSubmit={handleSubmit(handleSubmitForm)}>
      <div>
        <Dialog.Title className="m-0 text-2xl font-medium">
          Approve Payout Request
        </Dialog.Title>
        <div className={'flex flex-col mb-5 mt-6.5'}>
          <Dialog.Description className="text-xs leading-normal">
            Please check the following information for the payout on account{' '}
            {item?.leeloo_account.rithmic_account_name}
          </Dialog.Description>
          <div className={'flex flex-col gap-3 my-3'}>
            {item?.leeloo_user.is_flagged ? (
              <p className={'text-xl font-medium text-[#CD1A28]'}>
                User is flagged
              </p>
            ) : null}
            <p className={'text-xs'}>
              Account Payable Ration: {item?.payout_ratio}
            </p>
            <p className={'text-xs font-medium text-[#CD1A28]'}>
              Account failed 10% flipping rule!. Has{' '}
              {paAccounts?.payout_status?.flipping_days ?? 0} flipping days /{' '}
              {paAccounts?.payout_status?.flipping_days_percent ?? '0'}% of days
              in trading period
            </p>
          </div>
          <div className={'flex flex-col gap-3 mb-4'}>
            <p className={'text-xl font-medium text-lime'}>
              This account has active add-ons
            </p>
            {item?.addons.map((item) => (
              <p key={item.id} className={'text-lime text-xs'}>
                {item.title}
              </p>
            ))}
          </div>
          <Divider />
          <div className={'flex justify-between'}>
            <div className={'flex-2'}>
              <div className={'flex flex-col gap-y-3 my-5'}>
                <div className={'flex flex-col gap-1.5'}>
                  <p className={'text-xl font-medium'}>
                    1. Payout Amount: ${formatCurrency(Number(item?.amount), 2)}
                  </p>
                  <p className={'text-xs flex gap-x-1'}>
                    Rithmic Deduction $
                    {formatCurrency(Number(item?.rithmic_deduction), 2)}
                    <p
                      className={
                        item?.rithmic_deduction_added_on
                          ? 'text-lime'
                          : 'text-[#CD1A28]'
                      }
                    >
                      {item?.rithmic_deduction_added_on
                        ? formatDate(item.rithmic_deduction_added_on)
                        : 'pending'}
                    </p>
                  </p>
                </div>
                <Controller
                  control={control}
                  name="amount_is_correct"
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      id="amount_is_correct"
                      label={'Amount is correct'}
                      onCheckedChange={(checked) => field.onChange(checked)}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="make_rithmic_deduction"
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      id="make_rithmic_deduction"
                      label={'Make Rithmic Deduction ?'}
                      onCheckedChange={(checked) => field.onChange(checked)}
                    />
                  )}
                />
              </div>
              <Divider />
              <div className="mt-4 mb-6">
                <Controller
                  control={control}
                  name="payment_method"
                  render={({ field }) => (
                    <RadioGroup
                      className="grid grid-cols-3 gap-4"
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        setValue('payment_method_note', '');
                      }}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem id="riseworks" value="riseworks" />
                        <Label htmlFor="riseworks">Rise Work</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem id="paypal" value="paypal" />
                        <Label htmlFor="paypal">PayPal</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem id="wire" value="wire" />
                        <Label htmlFor="wire">Wire</Label>
                      </div>
                    </RadioGroup>
                  )}
                />
                <Controller
                  control={control}
                  name="payment_method_note"
                  render={({ field }) => (
                    <div
                      className={
                        paymentMethod && paymentMethod !== 'riseworks'
                          ? 'flex flex-col'
                          : 'hidden'
                      }
                    >
                      <Label
                        className="text-xs mb-2 mt-4"
                        htmlFor="deny_reason"
                      >
                        Note
                      </Label>
                      <Textarea
                        {...field}
                        hiddenHeader
                        className={'h-20'}
                        id="payment_method_note"
                        placeholder={'Enter'}
                      />
                    </div>
                  )}
                />
              </div>
              <Divider />
              <Controller
                control={control}
                name="approve_action"
                render={({ field }) => (
                  <div className={'flex flex-col mt-6 gap-y-5.5'}>
                    <p className={'text-xl font-medium'}>
                      Approve or Deny Request
                    </p>
                    <InputSelect
                      className={'w-full'}
                      options={[
                        { label: 'Approve', value: 'approve' },
                        { label: 'Deny', value: 'deny' },
                      ]}
                      placeholder={'Select'}
                      value={field.value}
                      onChange={(val) => {
                        field.onChange(val);
                        setValue('deny_reason', '');
                      }}
                    />
                    {errors.approve_action?.message ? (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.approve_action.message}
                      </p>
                    ) : null}
                  </div>
                )}
              />
              {/* Rithmic Credit Back Checkbox (DENY, PA, deduction exists, not credited) */}
              {approveAction === 'deny' &&
                !!item?.rithmic_deduction_added_on &&
                !item?.rithmic_deduction_credited_on && (
                  <Controller
                    control={control}
                    name="cb_rithmic_credit"
                    render={({ field }) => (
                      <Checkbox
                        checked={field.value}
                        id="cb_rithmic_credit"
                        label={"Credit Rithmic Deduction back to account?"}
                        onCheckedChange={(checked) => field.onChange(checked)}
                      />
                    )}
                  />
                )}
              <Controller
                control={control}
                name="deny_reason"
                render={({ field }) => (
                  <div
                    className={
                      approveAction === 'deny' ? 'flex flex-col' : 'hidden'
                    }
                  >
                    <Label className="text-xs mb-2 mt-4" htmlFor="deny_reason">
                      Reason you are denying this payout
                    </Label>
                    <Textarea
                      {...field}
                      hiddenHeader
                      className={'h-20'}
                      id="notes"
                      placeholder={'Enter'}
                    />
                  </div>
                )}
              />
            </div>
            <div className={'flex-1 py-5'}>
              <p className={'text-xl font-medium'}>User Details</p>
              <p className={'text-xs flex gap-x-1'}>
                {item?.leeloo_user.first_name} {item?.leeloo_user.last_name}
              </p>
              {item?.leeloo_user.street ? (
                <p className={'text-xs flex gap-x-1'}>
                  {item?.leeloo_user.street}
                </p>
              ) : null}
              {item?.leeloo_user.city ? (
                <p className={'text-xs flex gap-x-1'}>
                  {item?.leeloo_user.city}
                </p>
              ) : null}
              {item?.leeloo_user.state ? (
                <p className={'text-xs flex gap-x-1'}>
                  {item?.leeloo_user.street}
                </p>
              ) : null}
              {item?.leeloo_user.zip_code ? (
                <p className={'text-xs flex gap-x-1'}>
                  {item?.leeloo_user.zip_code}
                </p>
              ) : null}
              {item?.leeloo_user.country ? (
                <p className={'text-xs flex gap-x-1'}>
                  {item?.leeloo_user.country}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <Dialog.Close asChild>
        <Button
          aria-label="Close"
          className="[&_svg]:!size-auto absolute right-[35px] top-[43px] inline-flex size-[25px] appearance-none items-center justify-center rounded-full"
          variant={'ghost'}
          onClick={() => handleOpenChange(false)}
        >
          <CloseIcon />
        </Button>
      </Dialog.Close>
      <div className={'flex mt-[77px] gap-5'}>
        {approveAction === 'approve' ? (
          <Button
            aria-label="Approve"
            size={'sm'}
            type={'submit'}
            variant={'lime'}
          >
            Approve
          </Button>
        ) : null}
        {approveAction === 'deny' ? (
          <Button
            aria-label="Approve"
            size={'sm'}
            type={'submit'}
            variant={'secondary'}
          >
            Deny
          </Button>
        ) : null}
        <Button
          aria-label="Close"
          className="inline-flex appearance-none items-center justify-center bg-[#C13839] text-xs leading-[150%]"
          size={'sm'}
          type={'button'}
          onClick={() => handleOpenChange(false)}
        >
          Close
        </Button>
      </div>
    </form>
  );
};
